/**
 * sky.* Comp Key Handlers
 *
 *   sky.sun             — "x,y,z" unit direction FROM origin TO sun
 *   sky.sunIlluminance  — sun illuminance multiplier (default 20)
 *   sky.cameraAltitude  — km from planet center (default bottomRadius + 0)
 *
 * These keys configure the engine-wide SkyModule. They don't create
 * per-entity state; the `<x-sky>` element is just the DOM location where
 * the settings live.
 */

import type { HtmlRegistry } from '../../../html/registry/HtmlRegistry.js';
import type { CompKeyHandler } from '../../../html/types.js';
import type { SkyModule } from '../SkyModule.js';

export function registerSkyHtml(
  registry: HtmlRegistry,
  module: SkyModule,
): void {
  const sunHandler: CompKeyHandler = {
    key: 'sky.sun',
    validate: (v) => {
      const parts = v.split(',').map((s) => parseFloat(s.trim()));
      return parts.length === 3 && parts.every(Number.isFinite) ? null : 'expected "x,y,z"';
    },
    apply: (_entityId, value) => {
      const [x, y, z] = value.split(',').map((s) => parseFloat(s.trim()));
      const len = Math.sqrt(x * x + y * y + z * z) || 1;
      module.setSunDirection({ x: x / len, y: y / len, z: z / len });
    },
    remove: () => {
      // Reset to zenith as a sensible default
      module.setSunDirection({ x: 0, y: 1, z: 0 });
    },
  };

  const illuminanceHandler: CompKeyHandler = {
    key: 'sky.sunIlluminance',
    validate: (v) => (Number.isFinite(parseFloat(v)) ? null : 'expected number'),
    apply: (_entityId, value) => {
      module.setSunIlluminance(Math.max(0, parseFloat(value)));
    },
    remove: () => module.setSunIlluminance(20),
  };

  const altitudeHandler: CompKeyHandler = {
    key: 'sky.cameraAltitude',
    validate: (v) => (Number.isFinite(parseFloat(v)) ? null : 'expected number'),
    apply: (_entityId, value) => {
      const km = Math.max(0, parseFloat(value));
      module.setCameraAltitudeKm(module.atmosphere.bottomRadius + km);
    },
    remove: () => {
      module.setCameraAltitudeKm(module.atmosphere.bottomRadius + 0.001);
    },
  };

  registry.registerCompKey(sunHandler);
  registry.registerCompKey(illuminanceHandler);
  registry.registerCompKey(altitudeHandler);
}
